import { NestedKeysOf } from '@hooks';

import { Form, FormInstance } from 'antd';
import { get, isArray } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';

export type UseFormListInstance<T> = FormInstance<Record<string, T[]>> & {
	getItem: (fieldName: number) => T;
	getItemState: (fieldName: number, defaultEditState?: boolean) => recordState;
	isValid: boolean;
	state: recordState[],
	reset: () => void;
	setEdit: (fieldName: number, edit:boolean, useUpdater?: boolean) => void
	validate: () => void;
};

export interface UseFormListResult<T> {
	Form: typeof Form;
	form: UseFormListInstance<T>;
}

export interface UseFormListOptions<T> {
	required?: Array<NestedKeysOf<T>>;
	formListName: string;
	keyName: string;
}

interface recordState {
	key: number;
	isValid: boolean;
	isInEdit: boolean;
}

export function useFormList<T>(options: UseFormListOptions<T>): UseFormListResult<T> {
	const { required, formListName, keyName } = options;
	const [ state, setState ] = useState<recordState[]>([]);
	const [ form ] = Form.useForm();
	const requiredFields = useRef(required);

	const isValid = state.every((x) => x.isValid);

	const validateItem = useCallback((element: T, fieldName: number) => {
		const hasAllValues = requiredFields?.current ?
			Boolean(requiredFields.current?.every((name) => {
				const data = get(element, name);

				return isArray(data) ?
					data.length > 0 :
					Boolean(data);
			})) :
			true;

		const hasErrors = form.getFieldsError()?.filter(
			(entry) => entry.errors.length &&
			entry.name[ 1 ] === fieldName
		)?.length > 0;

		return hasAllValues && !hasErrors;

	}, [ form ]);

	const validate = useCallback(() => {
		setTimeout(() => {
			const values = form.getFieldValue(formListName)?.filter((x: T) => x !== undefined);
			const record: recordState[] = [];

			if (values?.length > 0) {
				values.forEach((element: T, index: number) => {

					const key = form.getFieldValue([ formListName, index, keyName ]);

					const isValidItem = validateItem(element, index);

					record.push(
						{
							isInEdit: state.find((x) => x.key === key)?.isInEdit ?? !isValidItem,
							isValid: isValidItem,
							key: key
						}
					);
				});
			}

			setState(record);
		});
	}, [ form, formListName, keyName, state, validateItem ]);

	const reset = useCallback(() => {
		form.resetFields();
	}, [ form ]);

	const setEdit = useCallback((fieldName: number, edit: boolean, useUpdater: boolean = false) => {
		const key = form.getFieldValue([ formListName, fieldName, keyName ]) ?? fieldName;

		if (useUpdater) {
			setState((prevRecords) => {
				let records = prevRecords;
				const index = records.findIndex((x) => x.key === key);

				if (index === -1) {
					const item = form.getFieldValue([ formListName, fieldName ]);

					records = [ ...records, {
						isInEdit: edit,
						isValid: validateItem(item, fieldName),
						key: key } ];
				} else {
					const record = {
						...records[ index ],
						isInEdit: edit
					};

					records[ index ] = record;
				}

				return records;
			});
		} else {
			let records = [ ...state ];
			const index = records.findIndex((x) => x.key === key);

			if (index === -1) {
				const item = form.getFieldValue([ formListName, fieldName ]);

				records = [ ...records, {
					isInEdit: edit,
					isValid: validateItem(item, fieldName),
					key: key } ];
			} else {
				const record = {
					...records[ index ],
					isInEdit: edit
				};

				records[ index ] = record;
			}

			setState(records);
		}

	}, [ form, formListName, keyName, state, validateItem ]);

	const getItemState = useCallback((fieldName: number, defaultEditState?: boolean) => {
		const key = form.getFieldValue([ formListName, fieldName, keyName ]);
		let records = [ ...state ];
		const itemState = records.find((x) => x.key === key);

		if (itemState === undefined) {
			const item = form.getFieldValue([ formListName, fieldName ]);
			const valid = validateItem(item, fieldName);

			const newRecord = {
				isInEdit: defaultEditState ?? !valid,
				isValid: valid,
				key: key };

			records = [ ...records, newRecord ];

			setState(records);

			return newRecord;
		}

		return itemState;

	}, [ form, formListName, keyName, state, validateItem ]);

	const getItem = useCallback((fieldName: number) =>
		form.getFieldValue([ formListName, fieldName ]),
	[ form, formListName ]);

	const formResult = useMemo(
		() => ({
			...form,
			getItem,
			getItemState,
			isValid,
			reset,
			setEdit,
			state,
			validate
		}),
		[ form, getItem, getItemState, isValid, reset, setEdit, state, validate ]
	);

	return {
		Form,
		form: formResult
	};
}
